import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import { MiniMarkdown } from "./MiniMarkdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "emmy_session_id";
const sessionId = () => {
  let id = localStorage.getItem(KEY);
  if (!id) { id = `emmy_${crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "") : Date.now().toString(36) + Math.random().toString(36).slice(2)}`; localStorage.setItem(KEY, id); }
  return id;
};
const FRIENDLY = "Oups, je n'arrive pas à vous répondre pour le moment. Veuillez réessayer dans un instant, ou contactez-nous au +225 07 87 80 10 87.";

function Bubble({ m, i }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} emmy-in`} data-testid={`emmy-msg-${m.role}-${i}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${mine ? "rounded-br-md bg-[#0369A1] text-white" : m.error ? "rounded-bl-md border border-amber-200 bg-amber-50 text-amber-900" : "rounded-bl-md border border-[#BAE6FD] bg-white text-slate-700"}`}>
        {mine ? <p className="whitespace-pre-wrap">{m.content}</p> : <MiniMarkdown text={m.content} />}
      </div>
    </div>
  );
}

const Typing = () => (
  <div className="flex justify-start" data-testid="emmy-typing">
    <div className="flex gap-1 rounded-2xl rounded-bl-md border border-[#BAE6FD] bg-white px-4 py-3">
      {[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-[#7DC4E8]" style={{ animationDelay: `${i * 0.15}s` }} />)}
    </div>
  </div>
);

export function EmmyWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [welcome, setWelcome] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const loaded = useRef(false);
  const end = useRef(null);
  const input = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open || loaded.current) return;
    loaded.current = true;
    fetch(`${API}/chat/${sessionId()}`).then((r) => r.json()).then((d) => { setWelcome(d.welcome); setMsgs(d.messages || []); })
      .catch(() => setWelcome("Bonjour, je suis **Emmy**, votre assistante Vitalis. Comment puis-je vous aider ?"));
  }, [open]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs, typing, open]);
  useEffect(() => { if (open) setTimeout(() => input.current?.focus(), 150); }, [open]);

  const send = useCallback(async () => {
    const q = text.trim();
    if (!q || busy) return;
    setText(""); setBusy(true); setTyping(true);
    setMsgs((m) => [...m, { role: "user", content: q }]);
    let started = false;
    // Updater idempotent (React StrictMode invoque les updaters deux fois) : on décide d'après l'état, pas d'un drapeau muté.
    const push = (patch) => {
      started = true; setTyping(false);
      setMsgs((m) => {
        const last = m[m.length - 1];
        if (!last || last.role === "user") return [...m, { role: "assistant", content: "", ...patch(null) }];
        return [...m.slice(0, -1), { ...last, ...patch(last) }];
      });
    };
    try {
      const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId(), message: q, page: pathname }) });
      if (!res.ok || !res.body) throw new Error(String(res.status));
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          const ev = JSON.parse(p.slice(5));
          if (ev.delta) push((last) => ({ content: (last?.content || "") + ev.delta }));
          if (ev.error) push(() => ({ content: ev.error, error: true }));
        }
      }
      if (!started) push(() => ({ content: FRIENDLY, error: true }));
    } catch {
      push(() => ({ content: FRIENDLY, error: true }));
    } finally { setBusy(false); setTyping(false); }
  }, [text, busy, pathname]);

  return (
    <>
      {open && (
        <div role="dialog" aria-label="Emmy – Agent support IA" data-testid="emmy-window"
          className="emmy-pop fixed inset-0 z-[60] flex flex-col overflow-hidden bg-[#F0F9FF] sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[550px] sm:max-h-[calc(100vh-7rem)] sm:w-[380px] sm:rounded-3xl sm:border sm:border-[#7DC4E8] sm:shadow-2xl">
          <div className="flex items-center gap-3 bg-[#0369A1] px-4 py-3 text-white">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 ring-2 ring-white/30"><Sparkles size={18} /></div>
            <div className="flex-1">
              <p className="text-sm font-bold leading-tight" data-testid="emmy-title">Emmy – Agent support IA</p>
              <p className="flex items-center gap-1.5 text-[11px] text-sky-100"><span className="h-1.5 w-1.5 rounded-full bg-emerald-300" /> En ligne · répond en quelques secondes</p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Fermer" data-testid="emmy-close" className="rounded-full p-1.5 transition-colors hover:bg-white/15"><X size={18} /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="emmy-messages">
            {welcome && <Bubble m={{ role: "assistant", content: welcome }} i="welcome" />}
            {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
            {typing && <Typing />}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 border-t border-[#BAE6FD] bg-white p-3">
            <textarea ref={input} rows={1} value={text} maxLength={2000} onChange={(e) => setText(e.target.value)} data-testid="emmy-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Écrivez votre message…" className="max-h-28 flex-1 resize-none rounded-2xl border border-[#BAE6FD] bg-[#F0F9FF] px-3.5 py-2.5 text-sm text-slate-700 outline-none transition-colors focus:border-[#0369A1]" />
            <button type="submit" disabled={busy || !text.trim()} aria-label="Envoyer" data-testid="emmy-send"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0369A1] text-white transition-transform hover:scale-105 disabled:opacity-40"><Send size={16} /></button>
          </form>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} aria-label={open ? "Fermer Emmy" : "Discuter avec Emmy"} data-testid="emmy-bubble"
        className={`fixed bottom-6 right-6 z-[61] h-14 w-14 items-center justify-center rounded-full bg-[#0369A1] text-white shadow-lg ring-4 ring-[#E0F2FE] transition-transform duration-200 hover:scale-110 ${open ? "hidden sm:flex" : "flex"}`}>
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>
    </>
  );
}
